import React, { useState } from "react";
import { Menu, MenuItem, ListItemText } from "@mui/material";
import { TripIcon } from "globals/design-system/icons";
import { white } from "globals/design-system/colors";
import { CircleButton } from "components/ui";
import {
  getGoogleMapsURL,
  getAppleMapsURL,
  getWazeURL,
  copyToClipboard,
  getLatLngFromCoordinates,
} from "globals/utils/helpers";
import { useSnackbar } from "globals/hooks/useSnackbar";

type Coordinates = { x: number; y: number };

type NavigationOptionsMenuProps = {
  address: string;
  displayAddress?: string;
  coordinates?: Coordinates;
};

function NavigationOptionsMenu({
  address,
  displayAddress,
  coordinates,
}: NavigationOptionsMenuProps) {
  // hooks
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const snackbar = useSnackbar();

  // state
  const open = Boolean(anchorEl);

  // handlers
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleGoogleMaps = () => {
    window.open(getGoogleMapsURL(address), "_blank");
    handleClose();
  };

  const handleAppleMaps = () => {
    const appleMapsDestination = coordinates
      ? getLatLngFromCoordinates(coordinates)
      : address;

    window.open(getAppleMapsURL(appleMapsDestination), "_blank");
    handleClose();
  };

  const handleWaze = () => {
    window.open(getWazeURL(address, coordinates), "_blank");
    handleClose();
  };

  const handleCopyAddress = async () => {
    const textToCopy = displayAddress || address;
    const success = await copyToClipboard(textToCopy);
    if (success) {
      snackbar.success("Address copied to clipboard");
    } else {
      snackbar.error("Failed to copy address");
    }
    handleClose();
  };

  return (
    <>
      <CircleButton
        onClick={handleClick}
        aria-controls={open ? "navigation-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <TripIcon color={white} />
      </CircleButton>
      <Menu
        id="navigation-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "navigation-button",
        }}
      >
        <MenuItem onClick={handleGoogleMaps}>
          <ListItemText>Google Maps</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAppleMaps}>
          <ListItemText>Apple Maps</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleWaze}>
          <ListItemText>Waze</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyAddress}>
          <ListItemText>Copy Address</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default NavigationOptionsMenu;